import useTenant from "src/hooks/api/services/tenants/useTenant";
import { cn } from "src/lib/utils";

type LogoVariant = "default" | "small" | "small-outline";
type LogoColor = "primary" | "white" | "dark";

interface LogoProps {
	color: LogoColor;
	variant?: LogoVariant;
	className?: string;
	pathClassName?: string;
	slug?: string;
}

const Logo = ({ variant = "default", ...props }: LogoProps) => {
	const { tenant } = useTenant();
	const slug = props.slug || tenant?.slug;

	if (slug === "getcrewdone") {
		return <GetCrewDone {...props} {...{ variant }} />;
	}

	if (slug === "getjobsdone") {
		return <GetJobsDone {...props} {...{ variant }} />;
	}

	if (slug === "odylyt") {
		return <Odylyt {...props} {...{ variant }} />;
	}

	if (slug === "getrecruitmentdone") {
		return <GetRecruitmentDone {...props} {...{ variant }} />;
	}

	if (slug === "getstagedone") {
		return <GetStageDone {...props} {...{ variant }} />;
	}

	if (variant === "small") {
		return (
			<div
				className={cn(
					"flex items-center justify-center",
					props?.className
				)}
			>
				<p className="font-bold mx-auto">{slug?.[0]}</p>
			</div>
		);
	}

	return <p className="font-bold mx-auto">{slug}</p>;
};

const GetStageDone = ({ variant, className }: LogoProps) => {
	if (variant === "small") {
		return (
			<img
				src="/icons/getstagedone/logo-small.png"
				alt="GetStagedone"
				className={cn("object-contain", className)}
			/>
		);
	}

	return (
		<img
			src="/icons/getstagedone/logo-full.png"
			alt="GetStagedone"
			className={cn("object-contain", className)}
		/>
	);
};

const GetRecruitmentDone = ({
	variant,
	className,
	color,
	pathClassName,
}: LogoProps) => {
	const fill = cn(`theme-getrecruitmentdone fill-${color}`, pathClassName);

	if (variant === "small") {
		return (
			<svg
				width="40"
				height="40"
				viewBox="0 0 40 40"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				{...{ className }}
				id="getrecruitmentdone"
			>
				<path
					d="M20.4049 39.7571C17.4899 39.7571 14.7908 39.2713 12.3077 38.2996C9.86055 37.292 7.7013 35.8884 5.82996 34.0891C3.9946 32.2897 2.55511 30.2024 1.51147 27.8273C0.503824 25.4161 0 22.807 0 20C0 17.157 0.521817 14.5479 1.56545 12.1727C2.64507 9.76159 4.13855 7.67432 6.04588 5.91093C7.95322 4.11157 10.1664 2.72605 12.6856 1.75439C15.2407 0.746743 17.9577 0.24292 20.8367 0.24292C24.2555 0.24292 27.3504 0.854705 30.1215 2.07828C32.8925 3.26586 35.1417 4.92128 36.8691 7.04454L30.3914 13.5223C29.0958 11.9748 27.6743 10.8232 26.1269 10.0675C24.5794 9.27576 22.78 8.87989 20.7287 8.87989C18.6415 8.87989 16.7701 9.34773 15.1147 10.2834C13.4953 11.1831 12.1997 12.4606 11.2281 14.1161C10.2564 15.7715 9.77058 17.7148 9.77058 19.946C9.77058 22.1772 10.2204 24.1386 11.1201 25.83C12.0558 27.5214 13.3153 28.8349 14.8988 29.7706C16.5182 30.7063 18.3716 31.1741 20.4588 31.1741C22.5461 31.1741 24.3095 30.7782 25.749 29.9865C27.2245 29.1948 28.3401 28.0432 29.0958 26.5317C29.8875 25.0202 30.2834 23.2209 30.2834 21.1336L36.8151 25.3441L19.3792 24.9123V17.031H40V18.4885C40 23.0589 39.1723 26.9276 37.5169 30.0945C35.8615 33.2614 33.5583 35.6725 30.6073 37.3279C27.6923 38.9474 24.2915 39.7571 20.4049 39.7571Z"
					className={fill}
				/>
			</svg>
		);
	}

	return (
		<svg
			width="590"
			height="42"
			viewBox="0 0 590 42"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			{...{ className }}
			id="getrecruitmentdone"
		>
			<path
				d="M20.2862 40.4021C17.4236 40.4021 14.7731 39.925 12.3346 38.9708C9.93139 37.9812 7.81095 36.6029 5.97323 34.8359C4.17086 33.0689 2.75723 31.0191 1.73235 28.6866C0.742816 26.3188 0.248047 23.7566 0.248047 21C0.248047 18.2081 0.760486 15.6459 1.78537 13.3135C2.84559 10.9456 4.31222 8.89587 6.18528 7.16417C8.05833 5.39714 10.2318 4.03653 12.7056 3.08233C15.2148 2.09279 17.883 1.59802 20.7103 1.59802C24.0677 1.59802 27.1069 2.19881 29.8282 3.40039C32.5494 4.56664 34.7582 6.19231 36.4546 8.27741L30.0932 14.6387C28.821 13.1191 27.425 11.9882 25.9054 11.246C24.3857 10.4685 22.6187 10.0798 20.6043 10.0798C18.5545 10.0798 16.7168 10.5392 15.0911 11.4581C13.5008 12.3416 12.2285 13.5962 11.2743 15.2218C10.3201 16.8475 9.84304 18.7559 9.84304 20.947C9.84304 23.1382 10.2848 25.0642 11.1683 26.7252C12.0872 28.3862 13.3241 29.6762 14.8791 30.595C16.4694 31.5139 18.2895 31.9733 20.3392 31.9733C22.389 31.9733 24.1207 31.5846 25.5343 30.8071C26.9833 30.0296 28.0788 28.8987 28.821 27.4144C29.5985 25.9301 29.9872 24.163 29.9872 22.1133L36.4015 26.2481L19.279 25.824V18.0844H39.5292V19.5157C39.5292 24.004 38.7164 27.8031 37.0907 30.9131C35.465 34.0231 33.2032 36.3909 30.3053 38.0166C27.4427 39.6069 24.103 40.4021 20.2862 40.4021Z"
				className={fill}
			/>
			<path
				d="M68.7022 33.4204V40.7424H47.6581V33.4204H68.7022ZM51.0479 1.25769V40.7424H41.5293V1.25769H51.0479ZM65.9903 16.9323V24.0102H47.6581V16.9323H65.9903ZM68.7835 1.25769V8.60684H47.6581V1.25769H68.7835Z"
				className={fill}
			/>
			<path
				d="M91.9086 1.25769V40.7424H82.39V1.25769H91.9086ZM103.787 1.25769V8.60684H70.7832V1.25769H103.787Z"
				className={fill}
			/>
			<path
				d="M112.594 1.25769L125.231 1.28481C127.563 1.33905 129.57 1.84526 131.251 2.80345C132.951 3.74356 134.226 5.08142 135.075 6.81701C135.925 8.53452 136.268 10.5775 136.106 12.9458C135.961 14.9526 135.581 16.7063 134.967 18.2068C134.352 19.7074 133.457 21.0181 132.282 22.139C131.125 23.2419 129.661 24.2181 127.889 25.0678L124.635 26.8034H113.896L115.116 19.4543L121.95 19.5356C123.071 19.4995 123.975 19.2193 124.662 18.695C125.349 18.1526 125.873 17.4565 126.235 16.6068C126.596 15.739 126.831 14.808 126.94 13.8136C127.048 13.1447 127.129 12.4215 127.184 11.6441C127.238 10.8486 127.111 10.1707 126.804 9.61023C126.497 9.0317 125.873 8.70627 124.933 8.63396L120.404 8.60684L114.79 40.7424H105.787L112.594 1.25769ZM122.926 40.7424L119.184 23.278L128.35 23.2238L132.607 40.2814V40.7424H122.926Z"
				className={fill}
			/>
			<path
				d="M162.063 33.4204L160.789 40.7424H143.975L145.277 33.4204H162.063ZM154.009 1.25769L147.148 40.7424H138.145L144.951 1.25769H154.009ZM162.741 16.9323L161.521 24.0102H146.904L148.151 16.9323H162.741ZM167.731 1.25769L166.456 8.60684H149.534L150.836 1.25769H167.731Z"
				className={fill}
			/>
			<path
				d="M187.562 27.4139L196.376 27.3054C196.303 30.1438 195.589 32.6297 194.233 34.763C192.895 36.8783 191.106 38.5144 188.864 39.6715C186.64 40.8105 184.118 41.3529 181.298 41.2986C178.839 41.2444 176.814 40.711 175.223 39.6986C173.632 38.6681 172.403 37.3122 171.535 35.6308C170.667 33.9314 170.116 32.0512 169.881 29.9902C169.664 27.9291 169.682 25.85 169.935 23.7529L170.803 18.2478C171.128 16.0602 171.68 13.9178 172.457 11.8207C173.252 9.70542 174.31 7.79808 175.63 6.09864C176.95 4.39921 178.55 3.06136 180.43 2.08509C182.328 1.10882 184.516 0.647799 186.993 0.702036C189.867 0.756273 192.199 1.41616 193.989 2.6817C195.779 3.94723 197.09 5.64667 197.921 7.78C198.753 9.91333 199.16 12.3088 199.142 14.9664L190.057 14.9122C190.22 13.7551 190.265 12.6794 190.193 11.6851C190.138 10.6726 189.831 9.84101 189.27 9.19017C188.728 8.53932 187.761 8.18678 186.369 8.13254C185.049 8.0783 183.991 8.37661 183.196 9.02745C182.4 9.6783 181.795 10.5371 181.379 11.6037C180.963 12.6523 180.656 13.7642 180.457 14.9393C180.276 16.1145 180.122 17.1992 179.996 18.1935L179.182 23.78C179.092 24.5574 178.947 25.5065 178.748 26.6274C178.568 27.7483 178.486 28.8512 178.504 29.9359C178.541 31.0207 178.794 31.9427 179.264 32.702C179.752 33.4433 180.62 33.85 181.867 33.9224C183.241 33.9585 184.335 33.6964 185.148 33.1359C185.962 32.5574 186.55 31.7709 186.911 30.7766C187.291 29.7642 187.508 28.6433 187.562 27.4139Z"
				className={fill}
			/>
			<path
				d="M207.949 1.25769L220.587 1.28481C222.919 1.33905 224.926 1.84526 226.607 2.80345C228.306 3.74356 229.581 5.08142 230.431 6.81701C231.28 8.53452 231.624 10.5775 231.461 12.9458C231.317 14.9526 230.937 16.7063 230.322 18.2068C229.708 19.7074 228.813 21.0181 227.637 22.139C226.48 23.2419 225.016 24.2181 223.244 25.0678L219.99 26.8034H209.251L210.471 19.4543L217.305 19.5356C218.426 19.4995 219.33 19.2193 220.017 18.695C220.704 18.1526 221.228 17.4565 221.59 16.6068C221.952 15.739 222.187 14.808 222.295 13.8136C222.404 13.1447 222.485 12.4215 222.539 11.6441C222.593 10.8486 222.467 10.1707 222.16 9.61023C221.852 9.0317 221.228 8.70627 220.288 8.63396L215.76 8.60684L210.146 40.7424H201.143L207.949 1.25769ZM218.282 40.7424L214.539 23.278L223.705 23.2238L227.963 40.2814V40.7424H218.282Z"
				className={fill}
			/>
			<path
				d="M255.15 0.958496H264.207L259.624 27.8331C259.263 30.563 258.458 32.9314 257.211 34.9381C255.981 36.9268 254.336 38.4545 252.275 39.5212C250.214 40.5698 247.774 41.076 244.953 41.0398C242.296 40.9856 240.072 40.4161 238.282 39.3314C236.492 38.2285 235.191 36.7008 234.377 34.7483C233.582 32.7777 233.328 30.4726 233.618 27.8331L238.174 0.958496H247.285L242.757 27.8602C242.63 28.8003 242.558 29.7133 242.54 30.5992C242.522 31.4669 242.72 32.1901 243.136 32.7686C243.552 33.3291 244.357 33.6274 245.55 33.6636C246.815 33.7359 247.783 33.5099 248.452 32.9856C249.12 32.4613 249.609 31.7472 249.916 30.8432C250.223 29.9212 250.449 28.9268 250.594 27.8602L255.15 0.958496Z"
				className={fill}
			/>
			<path
				d="M282.099 1.25769L275.265 40.7424H266.207L273.068 1.25769H282.099Z"
				className={fill}
			/>
			<path
				d="M303.515 1.25769L296.654 40.7424H287.569L294.43 1.25769H303.515ZM312.789 1.25769L311.515 8.60684H284.098L285.399 1.25769H312.789Z"
				className={fill}
			/>
			<path
				d="M326.26 1.25769H333.338L334.559 28.6475L345.325 1.25769H352.891L335.752 40.7424H329.759L326.26 1.25769ZM321.623 1.25769H329.216L325.338 31.9289L323.82 40.7424H314.789L321.623 1.25769ZM349.637 1.25769H357.284L350.45 40.7424H341.338L343.047 31.1424L349.637 1.25769Z"
				className={fill}
			/>
			<path
				d="M383.204 33.4204L381.929 40.7424H365.116L366.417 33.4204H383.204ZM375.15 1.25769L368.289 40.7424H359.285L366.092 1.25769H375.15ZM383.882 16.9323L382.661 24.0102H368.044L369.292 16.9323H383.882ZM388.872 1.25769L387.597 8.60684H370.675L371.977 1.25769H388.872Z"
				className={fill}
			/>
			<path
				d="M425.312 1.25769L418.478 40.7424H409.664L404.241 15.8204L399.902 40.7424H390.871L397.678 1.25769H406.546L411.969 26.1797L416.281 1.25769H425.312Z"
				className={fill}
			/>
			<path
				d="M446.727 1.25769L439.866 40.7424H430.782L437.643 1.25769H446.727ZM456.002 1.25769L454.727 8.60684H427.311L428.612 1.25769H456.002Z"
				className={fill}
			/>
			<path
				d="M470.829 40.7424H462.07L462.124 33.4204H470.829C472.637 33.4204 474.165 32.9865 475.412 32.1187C476.678 31.2509 477.636 29.9582 478.287 28.2407C478.956 26.5051 479.29 24.3718 479.29 21.8407V20.1323C479.29 18.2339 479.1 16.5707 478.721 15.1424C478.359 13.6961 477.817 12.4938 477.093 11.5357C476.37 10.5594 475.466 9.82718 474.382 9.33904C473.315 8.85091 472.086 8.60684 470.693 8.60684H461.907V1.25769H470.693C473.369 1.25769 475.819 1.71871 478.043 2.64074C480.284 3.54469 482.219 4.84639 483.846 6.54582C485.491 8.22718 486.757 10.2249 487.643 12.539C488.547 14.8351 488.999 17.3842 488.999 20.1865V21.8407C488.999 24.6249 488.547 27.1741 487.643 29.4882C486.757 31.8023 485.5 33.8 483.873 35.4814C482.246 37.1628 480.321 38.4644 478.097 39.3865C475.891 40.2904 473.469 40.7424 470.829 40.7424ZM467.521 1.25769V40.7424H458.002V1.25769H467.521Z"
				className={fill}
			/>
			<path
				d="M525.52 20.2136V21.8136C525.52 24.8328 525.095 27.5447 524.245 29.9492C523.414 32.3357 522.23 34.3786 520.693 36.078C519.156 37.7594 517.339 39.052 515.242 39.956C513.145 40.8419 510.831 41.2848 508.3 41.2848C505.751 41.2848 503.418 40.8419 501.303 39.956C499.206 39.052 497.38 37.7594 495.825 36.078C494.288 34.3786 493.095 32.3357 492.245 29.9492C491.414 27.5447 490.998 24.8328 490.998 21.8136V20.2136C490.998 17.1944 491.414 14.4826 492.245 12.078C493.095 9.67352 494.279 7.63058 495.798 5.94923C497.335 4.24979 499.152 2.95714 501.249 2.07126C503.364 1.16731 505.696 0.715332 508.245 0.715332C510.777 0.715332 513.091 1.16731 515.188 2.07126C517.303 2.95714 519.129 4.24979 520.666 5.94923C522.203 7.63058 523.396 9.67352 524.245 12.078C525.095 14.4826 525.52 17.1944 525.52 20.2136ZM515.866 21.8136V20.1594C515.866 18.2068 515.694 16.4893 515.351 15.0068C515.025 13.5063 514.537 12.2498 513.886 11.2374C513.235 10.2249 512.431 9.46561 511.473 8.95939C510.532 8.4351 509.457 8.17295 508.245 8.17295C506.98 8.17295 505.877 8.4351 504.937 8.95939C503.997 9.46561 503.21 10.2249 502.578 11.2374C501.945 12.2498 501.466 13.5063 501.14 15.0068C500.833 16.4893 500.679 18.2068 500.679 20.1594V21.8136C500.679 23.7481 500.833 25.4656 501.14 26.9662C501.466 28.4486 501.945 29.7051 502.578 30.7357C503.229 31.7662 504.024 32.5436 504.964 33.0679C505.922 33.5921 507.034 33.8543 508.3 33.8543C509.511 33.8543 510.587 33.5921 511.527 33.0679C512.467 32.5436 513.253 31.7662 513.886 30.7357C514.537 29.7051 515.025 28.4486 515.351 26.9662C515.694 25.4656 515.866 23.7481 515.866 21.8136Z"
				className={fill}
			/>
			<path
				d="M560.498 1.25769V40.7424H551.006L537.04 16.1729V40.7424H527.521V1.25769H537.04L551.006 25.8272V1.25769H560.498Z"
				className={fill}
			/>
			<path
				d="M589.669 33.4204V40.7424H568.625V33.4204H589.669ZM572.015 1.25769V40.7424H562.496V1.25769H572.015ZM586.957 16.9323V24.0102H568.625V16.9323H586.957ZM589.75 1.25769V8.60684H568.625V1.25769H589.75Z"
				className={fill}
			/>
		</svg>
	);
};

const GetCrewDone = ({
	variant,
	className,
	color,
	pathClassName,
}: LogoProps) => {
	const fill = cn(`theme-getcrewdone fill-${color}`, pathClassName);

	if (variant === "small") {
		return (
			<svg
				id="getcrewdone"
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 912.09 1112.92"
				{...{ className }}
			>
				<g id="Laag_1-2">
					<g>
						<path
							className={fill}
							d="m62.63,377.21c9.46-108.72,46.76-182.49,122.45-258.91C271.57,30.98,382.04,0,513.26,0c78.55,0,151.25,18.18,218.12,54.53,66.85,36.35,119.5,85.87,157.95,148.55l-173,100.29c-19.23-34.26-46.81-60.8-82.73-79.6-35.94-18.8-76.06-28.2-120.34-28.2-77.72,0-129.6,14.61-181.56,61.01-16.54,14.77-22.64,24.61-32.95,43.94l-236.11,76.7Z"
						/>
						<polygon
							className={fill}
							points="0 700.25 140.72 622.88 33.1 1112.92 394.44 435.32 250.57 504.12 295.47 339.05 62.59 421.67 0 700.25"
						/>
						<path
							className={fill}
							d="m357.95,586.2c46.16,47.85,100.31,64.22,178.03,64.59,44.29.21,84.66-9.2,121.1-28.26,36.45-19.04,63.93-45.24,82.48-78.59l172.52,101.1c-37.9,62.5-90.37,111.77-157.39,147.81-67.04,36.04-140.24,53.87-219.63,53.5-106.27-.5-189.41-23.48-266.08-81.12l-8.31-6.09,97.28-172.93"
						/>
					</g>
				</g>
			</svg>
		);
	}

	if (color === "dark") {
		return (
			<svg
				id="getcrewdone"
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 2736.44 342.97"
				{...{ className }}
			>
				<g id="Laag_1-2">
					<g>
						<path
							className="fill-dark"
							d="m263.74,118.19v21.89c0,38.18-11.86,68.64-35.57,91.38-23.71,22.74-53.81,34.11-90.28,34.11-39.64,0-72.53-12.77-98.67-38.3C13.07,201.72,0,170.35,0,133.15S12.95,64.45,38.85,38.67C64.75,12.89,96.42,0,133.88,0c23.83,0,45.66,5.35,65.48,16.05,19.82,10.7,35.2,24.81,46.15,42.31l-49.61,28.45c-5.6-8.75-13.92-15.92-24.99-21.52-11.07-5.59-23.53-8.39-37.39-8.39-21.65,0-39.58,7.18-53.81,21.52-14.23,14.35-21.34,32.71-21.34,55.08s7.3,40.19,21.89,54.17c14.59,13.99,34.04,20.98,58.36,20.98,33.8,0,55.57-13.13,65.3-39.4h-68.21v-51.07h128.04Z"
						/>
						<path
							className="fill-dark"
							d="m354.93,204.28h102.14v56.18h-160.5V5.11h158.68v56.18h-100.31v42.31h91.2v55.45h-91.2v45.23Z"
						/>
						<path
							className="fill-dark"
							d="m661.35,5.11v56.18h-65.66v199.17h-58.36V61.28h-65.66V5.11h189.69Z"
						/>
						<path
							className="fill-dark"
							d="m1887.24,5.11c35.26,0,64.75,12.22,88.46,36.66,23.71,24.44,35.57,54.78,35.57,91.01s-11.85,66.57-35.57,91.01c-23.71,24.44-53.2,36.66-88.46,36.66h-102.14V5.11h102.14Zm0,199.17c20.18,0,36.53-6.5,49.06-19.52,12.52-13.01,18.79-30.33,18.79-51.98s-6.26-38.97-18.79-51.98c-12.53-13.01-28.88-19.52-49.06-19.52h-43.77v143h43.77Z"
						/>
						<path
							className="fill-dark"
							d="m2260.04,227.26c-25.78,25.53-57.15,38.3-94.11,38.3s-68.34-12.77-94.11-38.3c-25.78-25.54-38.67-57.03-38.67-94.48s12.89-68.94,38.67-94.48c25.77-25.53,57.15-38.3,94.11-38.3s68.33,12.77,94.11,38.3c25.77,25.53,38.67,57.03,38.67,94.48s-12.89,68.94-38.67,94.48Zm-147.19-39.76c14.23,14.11,31.92,21.16,53.08,21.16s38.85-7.05,53.08-21.16c14.23-14.1,21.34-32.34,21.34-54.72s-7.11-40.61-21.34-54.72c-14.23-14.1-31.92-21.16-53.08-21.16s-38.85,7.06-53.08,21.16c-14.23,14.11-21.34,32.35-21.34,54.72s7.11,40.62,21.34,54.72Z"
						/>
						<path
							className="fill-dark"
							d="m2473.8,5.11h58.36v255.35h-43.77l-98.49-138.62v138.62h-58.37V5.11h43.77l98.49,138.62V5.11Z"
						/>
						<path
							className="fill-dark"
							d="m2634.31,204.28h102.14v56.18h-160.5V5.11h158.68v56.18h-100.31v42.31h91.2v55.45h-91.2v45.23Z"
						/>
						<path
							className="fill-primary"
							d="m1468.48,257.25L1399.35,6.52h60.17l43.34,175.51,48.35-175.51h46.57l48.35,175.51,43.34-175.51h60.17l-69.13,250.73h-65.19l-40.83-147.93-40.83,147.93h-65.19Z"
						/>
						<path
							className="fill-primary"
							d="m1264.85,200.26h101.42v55.78h-159.38V2.49h157.57v55.78h-99.61v42.02h90.55v55.06h-90.55v44.92Z"
						/>
						<path
							className="fill-primary"
							d="m1104.75,256.04l-47.81-83.31h-27.53v83.31h-57.96V2.49h101.42c24.14,0,44.67,8.46,61.58,25.36,16.9,16.91,25.36,37.43,25.36,61.58,0,15.46-4.29,29.76-12.86,42.92-8.57,13.16-19.87,23.36-33.87,30.61l53.97,93.09h-62.3Zm-75.34-199.22v65.2h43.47c7.97,0,14.79-3.14,20.47-9.42,5.67-6.28,8.51-14,8.51-23.18s-2.84-16.9-8.51-23.18c-5.68-6.28-12.5-9.42-20.47-9.42h-43.47Z"
						/>
						<path
							className="fill-primary"
							d="m676.38,117.89c2.89-33.26,14.31-55.83,37.46-79.21,26.46-26.71,60.26-36.19,100.4-36.19,24.03,0,46.27,5.56,66.73,16.68,20.45,11.12,36.56,26.27,48.32,45.45l-52.93,30.68c-5.88-10.48-14.32-18.6-25.31-24.35-11-5.75-23.27-8.63-36.82-8.63-23.78,0-39.65,4.47-55.55,18.66-5.06,4.52-6.93,7.53-10.08,13.44l-72.23,23.47Z"
						/>
						<polygon
							className="fill-primary"
							points="657.22 216.72 700.27 193.05 667.34 342.97 777.89 135.67 733.88 156.72 747.61 106.22 676.37 131.49 657.22 216.72"
						/>
						<path
							className="fill-primary"
							d="m736.97,234.73l2.54,1.86c23.45,17.63,48.89,24.66,81.4,24.82,24.29.11,46.68-5.34,67.19-16.37,20.5-11.03,36.56-26.1,48.15-45.22l-52.78-30.93c-5.67,10.2-14.08,18.22-25.23,24.04-11.15,5.83-23.5,8.71-37.05,8.65-23.78-.11-40.34-5.12-54.47-19.76l-29.76,52.91Z"
						/>
					</g>
				</g>
			</svg>
		);
	}

	if (color === "white") {
		return (
			<svg
				id="getcrewdone"
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 2736.44 342.97"
				{...{ className }}
			>
				<g id="Laag_1-2">
					<g>
						<path
							className="fill-white"
							d="m263.74,118.19v21.89c0,38.18-11.86,68.64-35.57,91.38-23.71,22.74-53.81,34.11-90.28,34.11-39.64,0-72.53-12.77-98.67-38.3C13.07,201.72,0,170.35,0,133.15S12.95,64.45,38.85,38.67C64.75,12.89,96.42,0,133.88,0c23.83,0,45.66,5.35,65.48,16.05,19.82,10.7,35.2,24.81,46.15,42.31l-49.61,28.45c-5.6-8.75-13.92-15.92-24.99-21.52-11.07-5.59-23.53-8.39-37.39-8.39-21.65,0-39.58,7.18-53.81,21.52-14.23,14.35-21.34,32.71-21.34,55.08s7.3,40.19,21.89,54.17c14.59,13.99,34.04,20.98,58.36,20.98,33.8,0,55.57-13.13,65.3-39.4h-68.21v-51.07h128.04Z"
						/>
						<path
							className="fill-white"
							d="m354.93,204.28h102.14v56.18h-160.5V5.11h158.68v56.18h-100.31v42.31h91.2v55.45h-91.2v45.23Z"
						/>
						<path
							className="fill-white"
							d="m661.35,5.11v56.18h-65.66v199.17h-58.36V61.28h-65.66V5.11h189.69Z"
						/>
						<path
							className="fill-white"
							d="m1887.24,5.11c35.26,0,64.75,12.22,88.46,36.66,23.71,24.44,35.57,54.78,35.57,91.01s-11.85,66.57-35.57,91.01c-23.71,24.44-53.2,36.66-88.46,36.66h-102.14V5.11h102.14Zm0,199.17c20.18,0,36.53-6.5,49.06-19.52,12.52-13.01,18.79-30.33,18.79-51.98s-6.26-38.97-18.79-51.98c-12.53-13.01-28.88-19.52-49.06-19.52h-43.77v143h43.77Z"
						/>
						<path
							className="fill-white"
							d="m2260.04,227.26c-25.78,25.53-57.15,38.3-94.11,38.3s-68.34-12.77-94.11-38.3c-25.78-25.54-38.67-57.03-38.67-94.48s12.89-68.94,38.67-94.48c25.77-25.53,57.15-38.3,94.11-38.3s68.33,12.77,94.11,38.3c25.77,25.53,38.67,57.03,38.67,94.48s-12.89,68.94-38.67,94.48Zm-147.19-39.76c14.23,14.11,31.92,21.16,53.08,21.16s38.85-7.05,53.08-21.16c14.23-14.1,21.34-32.34,21.34-54.72s-7.11-40.61-21.34-54.72c-14.23-14.1-31.92-21.16-53.08-21.16s-38.85,7.06-53.08,21.16c-14.23,14.11-21.34,32.35-21.34,54.72s7.11,40.62,21.34,54.72Z"
						/>
						<path
							className="fill-white"
							d="m2473.8,5.11h58.36v255.35h-43.77l-98.49-138.62v138.62h-58.37V5.11h43.77l98.49,138.62V5.11Z"
						/>
						<path
							className="fill-white"
							d="m2634.31,204.28h102.14v56.18h-160.5V5.11h158.68v56.18h-100.31v42.31h91.2v55.45h-91.2v45.23Z"
						/>
						<path
							className="fill-primary"
							d="m1468.48,257.25L1399.35,6.52h60.17l43.34,175.51,48.35-175.51h46.57l48.35,175.51,43.34-175.51h60.17l-69.13,250.73h-65.19l-40.83-147.93-40.83,147.93h-65.19Z"
						/>
						<path
							className="fill-primary"
							d="m1264.85,200.26h101.42v55.78h-159.38V2.49h157.57v55.78h-99.61v42.02h90.55v55.06h-90.55v44.92Z"
						/>
						<path
							className="fill-primary"
							d="m1104.75,256.04l-47.81-83.31h-27.53v83.31h-57.96V2.49h101.42c24.14,0,44.67,8.46,61.58,25.36,16.9,16.91,25.36,37.43,25.36,61.58,0,15.46-4.29,29.76-12.86,42.92-8.57,13.16-19.87,23.36-33.87,30.61l53.97,93.09h-62.3Zm-75.34-199.22v65.2h43.47c7.97,0,14.79-3.14,20.47-9.42,5.67-6.28,8.51-14,8.51-23.18s-2.84-16.9-8.51-23.18c-5.68-6.28-12.5-9.42-20.47-9.42h-43.47Z"
						/>
						<path
							className="fill-primary"
							d="m676.38,117.89c2.89-33.26,14.31-55.83,37.46-79.21,26.46-26.71,60.26-36.19,100.4-36.19,24.03,0,46.27,5.56,66.73,16.68,20.45,11.12,36.56,26.27,48.32,45.45l-52.93,30.68c-5.88-10.48-14.32-18.6-25.31-24.35-11-5.75-23.27-8.63-36.82-8.63-23.78,0-39.65,4.47-55.55,18.66-5.06,4.52-6.93,7.53-10.08,13.44l-72.23,23.47Z"
						/>
						<polygon
							className="fill-primary"
							points="657.22 216.72 700.27 193.05 667.34 342.97 777.89 135.67 733.88 156.72 747.61 106.22 676.37 131.49 657.22 216.72"
						/>
						<path
							className="fill-primary"
							d="m736.97,234.73l2.54,1.86c23.45,17.63,48.89,24.66,81.4,24.82,24.29.11,46.68-5.34,67.19-16.37,20.5-11.03,36.56-26.1,48.15-45.22l-52.78-30.93c-5.67,10.2-14.08,18.22-25.23,24.04-11.15,5.83-23.5,8.71-37.05,8.65-23.78-.11-40.34-5.12-54.47-19.76l-29.76,52.91Z"
						/>
					</g>
				</g>
			</svg>
		);
	}

	return (
		<svg
			id="getcrewdone"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 2736.44 342.97"
			{...{ className }}
		>
			<g id="Laag_1-2">
				<g>
					<path
						className="fill-dark"
						d="m263.74,118.19v21.89c0,38.18-11.86,68.64-35.57,91.38-23.71,22.74-53.81,34.11-90.28,34.11-39.64,0-72.53-12.77-98.67-38.3C13.07,201.72,0,170.35,0,133.15S12.95,64.45,38.85,38.67C64.75,12.89,96.42,0,133.88,0c23.83,0,45.66,5.35,65.48,16.05,19.82,10.7,35.2,24.81,46.15,42.31l-49.61,28.45c-5.6-8.75-13.92-15.92-24.99-21.52-11.07-5.59-23.53-8.39-37.39-8.39-21.65,0-39.58,7.18-53.81,21.52-14.23,14.35-21.34,32.71-21.34,55.08s7.3,40.19,21.89,54.17c14.59,13.99,34.04,20.98,58.36,20.98,33.8,0,55.57-13.13,65.3-39.4h-68.21v-51.07h128.04Z"
					/>
					<path
						className="fill-dark"
						d="m354.93,204.28h102.14v56.18h-160.5V5.11h158.68v56.18h-100.31v42.31h91.2v55.45h-91.2v45.23Z"
					/>
					<path
						className="fill-dark"
						d="m661.35,5.11v56.18h-65.66v199.17h-58.36V61.28h-65.66V5.11h189.69Z"
					/>
					<path
						className="fill-dark"
						d="m1887.24,5.11c35.26,0,64.75,12.22,88.46,36.66,23.71,24.44,35.57,54.78,35.57,91.01s-11.85,66.57-35.57,91.01c-23.71,24.44-53.2,36.66-88.46,36.66h-102.14V5.11h102.14Zm0,199.17c20.18,0,36.53-6.5,49.06-19.52,12.52-13.01,18.79-30.33,18.79-51.98s-6.26-38.97-18.79-51.98c-12.53-13.01-28.88-19.52-49.06-19.52h-43.77v143h43.77Z"
					/>
					<path
						className="fill-dark"
						d="m2260.04,227.26c-25.78,25.53-57.15,38.3-94.11,38.3s-68.34-12.77-94.11-38.3c-25.78-25.54-38.67-57.03-38.67-94.48s12.89-68.94,38.67-94.48c25.77-25.53,57.15-38.3,94.11-38.3s68.33,12.77,94.11,38.3c25.77,25.53,38.67,57.03,38.67,94.48s-12.89,68.94-38.67,94.48Zm-147.19-39.76c14.23,14.11,31.92,21.16,53.08,21.16s38.85-7.05,53.08-21.16c14.23-14.1,21.34-32.34,21.34-54.72s-7.11-40.61-21.34-54.72c-14.23-14.1-31.92-21.16-53.08-21.16s-38.85,7.06-53.08,21.16c-14.23,14.11-21.34,32.35-21.34,54.72s7.11,40.62,21.34,54.72Z"
					/>
					<path
						className="fill-dark"
						d="m2473.8,5.11h58.36v255.35h-43.77l-98.49-138.62v138.62h-58.37V5.11h43.77l98.49,138.62V5.11Z"
					/>
					<path
						className="fill-dark"
						d="m2634.31,204.28h102.14v56.18h-160.5V5.11h158.68v56.18h-100.31v42.31h91.2v55.45h-91.2v45.23Z"
					/>
					<path
						className="fill-primary"
						d="m1468.48,257.25L1399.35,6.52h60.17l43.34,175.51,48.35-175.51h46.57l48.35,175.51,43.34-175.51h60.17l-69.13,250.73h-65.19l-40.83-147.93-40.83,147.93h-65.19Z"
					/>
					<path
						className="fill-primary"
						d="m1264.85,200.26h101.42v55.78h-159.38V2.49h157.57v55.78h-99.61v42.02h90.55v55.06h-90.55v44.92Z"
					/>
					<path
						className="fill-primary"
						d="m1104.75,256.04l-47.81-83.31h-27.53v83.31h-57.96V2.49h101.42c24.14,0,44.67,8.46,61.58,25.36,16.9,16.91,25.36,37.43,25.36,61.58,0,15.46-4.29,29.76-12.86,42.92-8.57,13.16-19.87,23.36-33.87,30.61l53.97,93.09h-62.3Zm-75.34-199.22v65.2h43.47c7.97,0,14.79-3.14,20.47-9.42,5.67-6.28,8.51-14,8.51-23.18s-2.84-16.9-8.51-23.18c-5.68-6.28-12.5-9.42-20.47-9.42h-43.47Z"
					/>
					<path
						className="fill-primary"
						d="m676.38,117.89c2.89-33.26,14.31-55.83,37.46-79.21,26.46-26.71,60.26-36.19,100.4-36.19,24.03,0,46.27,5.56,66.73,16.68,20.45,11.12,36.56,26.27,48.32,45.45l-52.93,30.68c-5.88-10.48-14.32-18.6-25.31-24.35-11-5.75-23.27-8.63-36.82-8.63-23.78,0-39.65,4.47-55.55,18.66-5.06,4.52-6.93,7.53-10.08,13.44l-72.23,23.47Z"
					/>
					<polygon
						className="fill-primary"
						points="657.22 216.72 700.27 193.05 667.34 342.97 777.89 135.67 733.88 156.72 747.61 106.22 676.37 131.49 657.22 216.72"
					/>
					<path
						className="fill-primary"
						d="m736.97,234.73l2.54,1.86c23.45,17.63,48.89,24.66,81.4,24.82,24.29.11,46.68-5.34,67.19-16.37,20.5-11.03,36.56-26.1,48.15-45.22l-52.78-30.93c-5.67,10.2-14.08,18.22-25.23,24.04-11.15,5.83-23.5,8.71-37.05,8.65-23.78-.11-40.34-5.12-54.47-19.76l-29.76,52.91Z"
					/>
				</g>
			</g>
		</svg>
	);
};

const GetJobsDone = ({
	variant,
	color,
	className,
	pathClassName,
}: LogoProps) => {
	const fill = cn(`fill-${color}`, pathClassName);

	if (variant === "small-outline") {
		return (
			<svg
				{...{ className }}
				id="getjobsdone"
				viewBox="0 0 382 399"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					className={fill}
					d="M153.484 398.59C95.6018 398.59 53.8306 378.235 25.7844 336.362C7.95367 309.825 0 282.602 0 248.102C0 187.58 24.9984 122.153 65.2389 77.3513C86.2383 53.5375 113.246 33.9274 143.298 20.6878C173.622 7.4105 206.352 0.540485 239.445 0.506836C271.341 0.506836 303.623 8.36894 328.011 22.0766C353.598 36.4581 368.272 56.0528 368.272 75.8357C368.272 102.156 342.914 127.041 316.093 127.041C301.633 127.041 296.863 124.167 266.106 101.598C256.674 94.6251 236.55 89.1737 220.274 89.1737C190.065 89.1737 159.115 107.578 135.361 139.669C115.672 166.316 104.377 202.038 104.377 237.673C104.377 284.94 127.414 309.923 170.997 309.923C209.141 309.923 236.333 290.101 255.967 247.727H225.759C207.192 247.727 201.479 247.156 192.292 241.907L191.985 241.72C182.307 235.485 176.295 223.425 176.295 210.247C176.295 195.307 184.438 179.802 197.549 169.777C207.622 161.914 216.539 159.517 235.672 159.517C238.05 159.517 240.65 159.552 243.618 159.624C339.287 160.178 340.934 160.388 341.473 160.456C358.107 162.594 373.347 174.484 378.572 189.388C382.809 200.691 382.64 206.529 380.09 226.445L368.272 318.663C366.317 333.883 365.22 338.624 359.747 346.401C353.546 355.614 341.824 361.561 329.857 361.561C328.324 361.561 326.793 361.463 325.272 361.267C311.556 359.5 299.505 350.191 295.266 338.091C295.115 337.684 294.969 337.284 294.829 336.893C288.234 347.335 279.868 356.541 270.109 364.095L269.685 364.401C253.082 375.442 234.933 383.947 215.832 389.637L214.874 389.922C214.376 390.069 213.913 390.195 213.521 390.299C193.992 395.852 173.784 398.643 153.484 398.59V398.59ZM239.445 14.0331C208.222 14.0643 177.342 20.5455 148.731 33.0721C120.457 45.5264 95.0694 63.9518 75.3141 86.3581C37.1695 128.822 13.4992 190.782 13.4992 248.102C13.4992 279.746 20.7429 304.64 36.9868 328.816C49.8168 347.971 65.4177 361.811 84.6872 371.136C104.054 380.508 126.557 385.064 153.484 385.064C172.549 385.115 191.528 382.493 209.869 377.273L210.013 377.233C210.682 377.057 211.156 376.92 211.577 376.782C211.827 376.694 212.082 376.621 212.341 376.563C229.94 371.282 246.669 363.438 261.991 353.282C278.252 340.665 289.821 322.948 294.847 302.965L295.244 301.39L308.328 304.72L308.086 305.676L307.422 310.823C305.52 325.559 305.734 327.568 307.926 333.4L307.982 333.561C310.515 340.858 318.334 346.734 326.995 347.85C327.943 347.972 328.897 348.033 329.852 348.032C337.358 348.032 344.884 344.32 348.578 338.795L348.672 338.658C352.381 333.398 353.077 330.962 354.878 316.934L366.699 224.718C369.144 205.62 369.062 202.47 365.908 194.078L365.852 193.92C362.352 183.854 351.493 175.466 339.976 173.898C335.55 173.727 280.135 173.358 243.475 173.145H243.349C240.465 173.075 237.953 173.041 235.668 173.041C218.39 173.041 212.825 174.987 205.815 180.467L205.758 180.513C196.061 187.919 189.796 199.59 189.796 210.245C189.796 218.668 193.461 226.497 199.148 230.249C204.953 233.547 207.809 234.199 225.76 234.199H276.316L272.514 243.52C250.583 297.303 217.377 323.448 170.997 323.448C120.08 323.448 90.8775 292.184 90.8775 237.672C90.8775 199.201 103.137 160.544 124.515 131.611C150.846 96.0451 185.747 75.6452 220.274 75.6452C239.666 75.6452 262.304 81.9758 274.101 90.6978C305.195 113.513 306.646 113.513 316.093 113.513C335.252 113.513 354.773 94.4972 354.773 75.834C354.773 61.0851 342.611 45.7908 321.407 33.8723C298.974 21.2651 269.1 14.0331 239.445 14.0331Z"
				/>
			</svg>
		);
	}

	if (variant === "small") {
		return (
			<svg
				{...{ className }}
				id="getjobsdone"
				viewBox="0 0 382 399"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					className={fill}
					d="M378.834 191.239C376.139 184.467 371.678 178.541 365.916 174.078C360.154 169.615 353.301 166.777 346.07 165.861C344.235 165.626 245.424 165.077 245.424 165.077C220.76 164.473 212.243 166.175 202.049 174.127C196.339 178.364 191.646 183.821 188.313 190.101C184.98 196.381 183.09 203.325 182.78 210.428C182.6 215.602 183.696 220.741 185.972 225.391C188.248 230.041 191.633 234.06 195.83 237.092C203.774 241.625 208.314 242.197 227.049 242.197H269.044C247.478 294.99 215.717 320.524 170.304 320.524C121.494 320.524 94.2347 291.563 94.2347 238.793C94.1059 200.979 105.818 164.074 127.729 133.254C153.827 98.0661 187.886 78.1941 221.356 78.1941C239.539 78.1129 257.394 83.0371 272.965 92.4276C304.742 115.695 308.153 117.397 320.638 117.397C332.743 116.806 344.212 111.803 352.881 103.334C361.55 94.8643 366.818 83.5142 367.691 71.4263C367.691 33.9722 308.09 0.501898 241.165 0.501898C208.166 0.312853 175.498 7.08577 145.294 20.3785C115.09 33.6711 88.0286 53.1847 65.8775 77.6451C24.2554 125.347 0.929223 186.282 0.0583522 249.584C-0.814268 280.673 8.11905 311.248 25.5924 336.977C53.385 378.399 94.815 398.271 152.141 398.271C172.56 398.33 192.885 395.527 212.526 389.943C213.247 389.755 213.953 389.559 214.659 389.324C214.698 389.315 214.738 389.315 214.776 389.324C233.911 383.637 252.092 375.135 268.723 364.095C286.97 350.031 299.935 330.219 305.518 307.867V307.867L304.773 313.608C302.773 329.049 302.758 332.546 305.683 340.326C307.791 345.5 311.235 350.022 315.663 353.429C320.091 356.837 325.344 359.008 330.886 359.721C336.428 360.434 342.059 359.663 347.205 357.487C352.352 355.312 356.828 351.809 360.178 347.337C364.985 340.538 365.903 336.742 367.832 321.724L380.073 226.293C382.669 206.037 382.708 201.457 378.866 191.247"
				/>
			</svg>
		);
	}

	return (
		<svg
			{...{ className }}
			id="getjobsdone"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 522.01 67.24"
		>
			<g data-name="Laag 2">
				<g id="Laag_1-2" data-name="Laag 1">
					<g id="Group_2" data-name="Group 2">
						<path
							id="Path_229"
							data-name="Path 229"
							className={fill}
							d="M92.76,19.43A28,28,0,0,0,73,27.93c-5.1,5.37-8.6,13.88-8.6,21a18.4,18.4,0,0,0,5,12.26c3.49,3.59,8.33,5.37,14.68,5.37,6.18,0,12.9-1.78,17.37-4.74,3-2,4.57-4,4.57-6.36,0-2.68-1.79-4.65-4.21-4.65a8.82,8.82,0,0,0-3,.62c-7.61,3.4-8.42,3.67-11.73,3.67-5.2,0-8-2.5-8.69-7.78h24.54c5.28,0,7.25-2.78,7.25-9.94C110.13,27,102.88,19.43,92.76,19.43Zm-13,19c3.13-5.55,6.72-8.24,10.92-8.24a6.71,6.71,0,0,1,5.37,2.33c1,1.25,1.35,2.77,1.44,5.91Z"
						/>
						<path
							id="Path_230"
							data-name="Path 230"
							className={fill}
							d="M148.24,25.16c0-3.49-2-4.84-6.8-4.84H139l1.43-6.8A11.46,11.46,0,0,0,141,10a5,5,0,0,0-4.76-5.19h-.43a8.43,8.43,0,0,0-6.45,3.22c-1,1.43-1.34,2.24-2,5.46l-1.52,6.8c-3.13,0-3.85.09-5.37.81a7.25,7.25,0,0,0-4.12,6.27c0,3.49,2.06,4.92,7,4.83L118,57.57a11.46,11.46,0,0,0-.54,3.49,5,5,0,0,0,4.72,5.28h.48a8.27,8.27,0,0,0,6.44-3.22c1.08-1.43,1.34-2.33,2.06-5.55l5.37-25.34h2.42c3.49,0,4.83-.27,6.36-1.43A7.4,7.4,0,0,0,148.24,25.16Z"
						/>
						<path
							id="Path_231"
							data-name="Path 231"
							className={fill}
							d="M183.23,10.56a5.19,5.19,0,0,0-4.92-5.45h-.45a8.63,8.63,0,0,0-6.8,3.4c-1,1.53-1.34,2.33-2.06,5.73l-6.89,32.5c-1.08,5-3.23,7.7-6.27,7.7A7,7,0,0,1,152,52.91c-1.88-1.25-2.6-1.52-4-1.52A8,8,0,0,0,140.08,59c0,4.74,5.91,8.24,14,8.24,5.82,0,11.55-2,14.86-5.2s5.28-7.79,6.89-15.31l6.9-32.5A13.74,13.74,0,0,0,183.23,10.56Z"
						/>
						<path
							id="Path_232"
							data-name="Path 232"
							className={fill}
							d="M212.67,19.43A28.12,28.12,0,0,0,192,29a32.26,32.26,0,0,0-7.16,19.78c0,10.21,7.43,17.82,17.37,17.82A27.73,27.73,0,0,0,222.79,57,32.57,32.57,0,0,0,230,37.42C230,27.13,222.61,19.43,212.67,19.43Zm-8,34.92c-3.85,0-6.63-3.23-6.63-7.61,0-7.79,5.55-15,11.64-15,4.3,0,7.08,3.22,7.08,8.23C216.79,47.27,210.88,54.35,204.71,54.35Z"
						/>
						<path
							id="Path_233"
							data-name="Path 233"
							className={fill}
							d="M264,19.43c-3.94,0-6.89,1-11.37,3.85l3-14.5a14.5,14.5,0,0,0,.54-3.58A5,5,0,0,0,251.41,0H251a8.3,8.3,0,0,0-6.45,3.23c-1,1.43-1.34,2.32-2,5.55L231.82,59.36a7.55,7.55,0,0,0-.27,1.88c0,3,2.33,5.1,5.55,5.1,3.05,0,5.19-1.52,7.16-4.92,2.06,3.67,4.93,5.19,9.76,5.19a24.37,24.37,0,0,0,18.18-8.68,33.89,33.89,0,0,0,7.88-20.68C280.08,27.13,273.09,19.43,264,19.43Zm-9,34.91c-4.12,0-7-3.22-7-7.79,0-7.7,5.73-14.86,12-14.86,4,0,6.72,3.14,6.72,8.06,0,7.43-5.73,14.6-11.73,14.6Z"
						/>
						<path
							id="Path_234"
							data-name="Path 234"
							className={fill}
							d="M308,19.43c-11.37,0-20.24,7.7-20.24,17.46,0,3.49,1.61,5.91,5.11,7.88a46,46,0,0,0,7.07,3.31c3.4,1.34,4.3,2.06,4.3,3.4,0,1.79-2.24,3.49-4.57,3.49a10.91,10.91,0,0,1-4.92-1.43c-4.66-2.33-4.66-2.33-6.27-2.33-3.31,0-6.8,3.58-6.8,6.8,0,4.74,6.8,8.59,15,8.59,6.18,0,12-2.14,15.49-5.73,3-3.22,5.1-7.78,5.1-11.46a8.85,8.85,0,0,0-2-5.64c-1.88-2.41-3.22-3.31-8.68-5.73s-5.82-2.68-5.82-4.2,1.7-2.78,4-2.78a10.29,10.29,0,0,1,4.65,1.34c3.67,1.7,3.76,1.79,5.19,1.79,3.05,0,6.36-3.49,6.36-6.62C321.07,23.1,315.25,19.43,308,19.43Z"
						/>
						<path
							id="Path_235"
							data-name="Path 235"
							className={fill}
							d="M355,6h-12.9A9.6,9.6,0,0,0,334.57,9c-1.43,1.52-2,2.87-2.86,7L323.29,55.6a18.4,18.4,0,0,0-.62,4.12,5.35,5.35,0,0,0,1.88,4.3c1.34,1.25,2.32,1.43,6.44,1.52l13.34.18a32.9,32.9,0,0,0,24.26-9.67,40.8,40.8,0,0,0,10.93-27.49C379.52,14.06,370.74,6,355,6Zm4.74,40a18,18,0,0,1-15.49,7.79h-6.8l7.61-35.9h5.19c10.12,0,14.6,3.85,14.6,12.35A27.75,27.75,0,0,1,359.73,46Z"
						/>
						<path
							id="Path_236"
							data-name="Path 236"
							className={fill}
							d="M409,19.43A28.1,28.1,0,0,0,388.28,29a32.26,32.26,0,0,0-7.16,19.78c0,10.21,7.43,17.82,17.36,17.82A27.75,27.75,0,0,0,419.08,57a32.57,32.57,0,0,0,7.16-19.61C426.24,27.13,418.9,19.43,409,19.43Zm-8,34.91c-3.85,0-6.62-3.22-6.62-7.61,0-7.78,5.55-15,11.64-15,4.3,0,7.07,3.23,7.07,8.24,0,7.34-5.91,14.42-12.09,14.42Z"
						/>
						<path
							id="Path_237"
							data-name="Path 237"
							className={fill}
							d="M461.32,19.43c-5.1,0-8.5,1.25-13.16,4.83-.63-3.13-2.24-4.56-5.1-4.56-4.21,0-7.34,2.86-8.33,7.7l-6.44,30.17a15.74,15.74,0,0,0-.45,3.49,5,5,0,0,0,4.72,5.28H433a8.39,8.39,0,0,0,6.45-3.22c1-1.43,1.25-2.33,2-5.55l4-19c.9-4.21,5.1-7.79,9.13-7.79,3.49,0,5.91,2.24,5.91,5.46a11.5,11.5,0,0,1-.36,2.86l-3.94,18.45a17,17,0,0,0-.45,3.49,5,5,0,0,0,4.72,5.28H461a8.37,8.37,0,0,0,6.44-3.22c1-1.43,1.25-2.33,2-5.55L474,36a27.73,27.73,0,0,0,.71-5.37C474.66,24.08,469.11,19.43,461.32,19.43Z"
						/>
						<path
							id="Path_238"
							data-name="Path 238"
							className={fill}
							d="M522,37.42c0-10.47-7.25-18-17.37-18a28,28,0,0,0-19.79,8.5c-5.1,5.37-8.59,13.88-8.59,21a18.47,18.47,0,0,0,5,12.26c3.5,3.59,8.33,5.37,14.69,5.37,6.17,0,12.89-1.78,17.36-4.74,3-2,4.57-4,4.57-6.36,0-2.68-1.79-4.65-4.21-4.65a8.82,8.82,0,0,0-3,.62c-7.61,3.4-8.42,3.67-11.73,3.67-5.19,0-8-2.5-8.68-7.78h24.53C520,47.36,522,44.59,522,37.42Zm-30.35,1c3.13-5.55,6.71-8.24,10.92-8.24A6.73,6.73,0,0,1,508,32.5c1,1.25,1.35,2.77,1.44,5.91Z"
						/>
						<path
							id="Path_239"
							data-name="Path 239"
							className={fill}
							d="M59.78,34.47a6.46,6.46,0,0,0-5.17-4c-.29,0-15.88-.12-15.88-.12-3.89-.1-5.24.17-6.85,1.42a7.57,7.57,0,0,0-3,5.73,5,5,0,0,0,2.06,4.21c1.25.72,2,.8,4.92.8h6.63C39.05,50.84,34,54.87,26.87,54.87c-7.7,0-12-4.57-12-12.9a28.62,28.62,0,0,1,5.28-16.65c4.12-5.55,9.49-8.69,14.77-8.69a15.7,15.7,0,0,1,8.15,2.24c5,3.67,5.55,3.94,7.52,3.94,3.76,0,7.43-3.58,7.43-7.25,0-5.91-9.4-11.19-20-11.19A37,37,0,0,0,10.39,16.55,42.12,42.12,0,0,0,0,43.68,23.35,23.35,0,0,0,4,57.47C8.43,64,15,67.13,24,67.13a34.46,34.46,0,0,0,9.53-1.31l.34-.1h0a32,32,0,0,0,8.52-4,16.16,16.16,0,0,0,5.8-8.87h0l-.12.9c-.31,2.44-.31,3,.15,4.22a5,5,0,0,0,8.6,1.11c.76-1.08.9-1.68,1.21-4.05L60,40C60.38,36.81,60.38,36.09,59.78,34.47Z"
						/>
					</g>
				</g>
			</g>
		</svg>
	);
};

const Odylyt = ({ variant, color, className, pathClassName }: LogoProps) => {
	const fill = cn(`fill-${color}`, pathClassName);

	if (variant === "small") {
		return (
			<svg
				{...{ className }}
				data-theme="odylyt"
				viewBox="0 0 88 73"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					className={fill}
					d="M34.4592 72.9111C27.4053 72.9111 21.3047 71.4336 16.1572 68.4786C11.1051 65.5236 7.14922 61.3294 4.28953 55.896C1.42984 50.4626 0 44.0759 0 36.7361C0 31.112 0.810245 26.1076 2.43073 21.7227C4.05122 17.3379 6.33897 13.5726 9.29398 10.427C12.3443 7.18598 15.9666 4.75524 20.1608 3.13475C24.4503 1.41894 29.2165 0.561035 34.4592 0.561035C41.4178 0.561035 47.4231 2.03854 52.4753 4.99355C57.6227 7.94856 61.6263 12.1428 64.4859 17.5762C67.3456 22.9143 68.7755 29.3009 68.7755 36.7361C68.7755 42.2648 67.9652 47.2693 66.3447 51.7494C64.7243 56.2296 62.3888 60.0425 59.3385 63.1882C56.3835 66.3338 52.7612 68.7646 48.4717 70.4804C44.2775 72.1009 39.6067 72.9111 34.4592 72.9111ZM34.4592 61.7583C38.3675 61.7583 41.7991 60.8051 44.7541 58.8987C47.7091 56.9922 49.9969 54.1802 51.6174 50.4626C53.3332 46.745 54.1911 42.1695 54.1911 36.7361C54.1911 28.5383 52.3799 22.3423 48.7577 18.1481C45.1354 13.9539 40.3693 11.8568 34.4592 11.8568C30.4557 11.8568 26.9764 12.81 24.0214 14.7165C21.0664 16.5276 18.731 19.292 17.0151 23.0096C15.3947 26.7272 14.5844 31.3027 14.5844 36.7361C14.5844 44.8385 16.3955 51.0345 20.0178 55.324C23.6401 59.6136 28.4539 61.7583 34.4592 61.7583Z"
				/>
				<path
					className={fill}
					d="M70.2287 67.4405C70.6391 68.7059 71.8865 69.5134 73.2174 69.5134H73.2697C73.5129 70.7161 74.5788 71.6243 75.8565 71.6243C76.2907 71.6243 76.6963 71.515 77.0554 71.3269C77.3623 71.1654 77.5447 70.8358 77.5447 70.489V53.5999C77.5447 53.1524 77.2369 52.7544 76.7989 52.666C76.3648 52.5777 75.8812 52.6242 75.3939 52.857C74.6871 53.1952 74.2729 53.9504 74.2729 54.7361V54.7883C74.1038 54.756 73.9233 54.7361 73.7447 54.7361C72.2883 54.7361 71.1065 55.9179 71.1065 57.3742C71.1065 57.987 71.3174 58.556 71.6661 58.9997C70.1565 59.2324 68.9947 60.5406 68.9947 62.1242C68.9947 63.2433 69.5751 64.2247 70.451 64.7956C70.0425 65.5281 69.9086 66.4544 70.2287 67.4405ZM85.3233 58.9997C85.6729 58.556 85.8829 57.987 85.8829 57.3742C85.8829 55.9179 84.701 54.7361 83.2447 54.7361C83.0661 54.7361 82.8856 54.756 82.7165 54.7883V54.7361C82.7165 53.9514 82.3013 53.1952 81.5945 52.857C81.1081 52.6242 80.6246 52.5777 80.1904 52.666C79.7525 52.7553 79.4447 53.1534 79.4447 53.5999V70.4881C79.4447 70.8348 79.6271 71.1645 79.9349 71.326C80.294 71.515 80.6987 71.6243 81.1328 71.6243C82.4106 71.6243 83.4765 70.7161 83.7197 69.5124H83.7729C85.1029 69.5124 86.3512 68.7049 86.7616 67.4395C87.0818 66.4534 86.9478 65.5271 86.5393 64.7947C87.4143 64.2247 87.9947 63.2433 87.9947 62.1242C87.9947 60.5406 86.8329 59.2324 85.3233 58.9997Z"
				/>
			</svg>
		);
	}

	return (
		<svg
			{...{ className }}
			data-theme="odylyt"
			viewBox="0 0 432 129"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M34.4592 103.092C27.4053 103.092 21.3047 101.614 16.1572 98.6592C11.1051 95.7042 7.14922 91.51 4.28953 86.0766C1.42984 80.6432 0 74.2565 0 66.9167C0 61.2926 0.810245 56.2882 2.43073 51.9033C4.05122 47.5185 6.33897 43.7532 9.29398 40.6076C12.3443 37.3666 15.9666 34.9358 20.1608 33.3154C24.4503 31.5995 29.2165 30.7416 34.4592 30.7416C41.4178 30.7416 47.4231 32.2191 52.4753 35.1742C57.6227 38.1292 61.6263 42.3234 64.4859 47.7568C67.3456 53.0949 68.7755 59.4815 68.7755 66.9167C68.7755 72.4454 67.9652 77.4499 66.3447 81.93C64.7243 86.4102 62.3888 90.2231 59.3385 93.3688C56.3835 96.5144 52.7612 98.9452 48.4717 100.661C44.2775 102.281 39.6067 103.092 34.4592 103.092ZM34.4592 91.9389C38.3675 91.9389 41.7991 90.9857 44.7541 89.0793C47.7091 87.1728 49.9969 84.3608 51.6174 80.6432C53.3332 76.9256 54.1911 72.3501 54.1911 66.9167C54.1911 58.7189 52.3799 52.5229 48.7577 48.3287C45.1354 44.1345 40.3693 42.0374 34.4592 42.0374C30.4557 42.0374 26.9764 42.9906 24.0214 44.8971C21.0664 46.7082 18.731 49.4726 17.0151 53.1902C15.3947 56.9078 14.5844 61.4833 14.5844 66.9167C14.5844 75.0191 16.3955 81.2151 20.0178 85.5046C23.6401 89.7942 28.4539 91.9389 34.4592 91.9389Z"
				className={fill}
			/>
			<path
				d="M111.589 103.092C105.489 103.092 100.103 101.662 95.432 98.8022C90.8565 95.8472 87.2819 91.653 84.7082 86.2196C82.1345 80.6908 80.8476 74.2565 80.8476 66.9167C80.8476 59.3862 82.1345 52.9519 84.7082 47.6138C87.2819 42.2757 90.8565 38.1292 95.432 35.1742C100.103 32.2191 105.489 30.7416 111.589 30.7416C117.69 30.7416 122.933 32.2668 127.318 35.3171C131.798 38.2721 134.8 42.2757 136.326 47.3278H134.753V7.2922C134.753 4.90913 135.372 3.09799 136.612 1.85879C137.851 0.619597 139.614 0 141.902 0C144.19 0 145.953 0.619597 147.192 1.85879C148.527 3.09799 149.194 4.90913 149.194 7.2922V95.5135C149.194 97.8013 148.575 99.6124 147.335 100.947C146.096 102.186 144.333 102.806 142.045 102.806C139.757 102.806 137.994 102.186 136.754 100.947C135.515 99.6124 134.896 97.8013 134.896 95.5135V82.9309L136.469 86.0766C134.943 91.224 131.941 95.3706 127.461 98.5162C123.076 101.567 117.785 103.092 111.589 103.092ZM115.307 91.9389C119.215 91.9389 122.647 90.9857 125.602 89.0793C128.557 87.1728 130.844 84.3608 132.465 80.6432C134.181 76.9256 135.039 72.3501 135.039 66.9167C135.039 58.7189 133.228 52.5229 129.605 48.3287C125.983 44.1345 121.217 42.0374 115.307 42.0374C111.303 42.0374 107.824 42.9906 104.869 44.8971C101.914 46.7082 99.5786 49.4726 97.8627 53.1902C96.2422 56.9078 95.432 61.4833 95.432 66.9167C95.432 75.0191 97.2431 81.2151 100.865 85.5046C104.488 89.7942 109.301 91.9389 115.307 91.9389Z"
				className={fill}
			/>
			<path
				d="M185.915 128.543C184.2 128.543 182.77 128.066 181.626 127.113C180.577 126.255 179.958 125.064 179.767 123.538C179.672 122.109 179.958 120.536 180.625 118.82L190.348 96.9434V103.092L163.61 40.8935C162.943 39.1777 162.704 37.5572 162.895 36.0321C163.086 34.5069 163.801 33.3154 165.04 32.4575C166.279 31.5042 167.947 31.0276 170.044 31.0276C171.855 31.0276 173.285 31.4566 174.334 32.3145C175.382 33.1724 176.335 34.6499 177.193 36.747L198.784 90.0801H194.78L216.657 36.747C217.515 34.5546 218.516 33.077 219.66 32.3145C220.804 31.4566 222.376 31.0276 224.378 31.0276C226.094 31.0276 227.429 31.5042 228.382 32.4575C229.43 33.3154 230.05 34.5069 230.241 36.0321C230.527 37.4619 230.288 39.0347 229.526 40.7505L193.78 122.967C192.826 125.159 191.73 126.636 190.491 127.399C189.347 128.162 187.822 128.543 185.915 128.543Z"
				className={fill}
			/>
			<path
				d="M266.206 103.092C258.77 103.092 253.146 100.947 249.333 96.6574C245.616 92.2726 243.757 85.9813 243.757 77.7835V7.2922C243.757 4.90913 244.377 3.09799 245.616 1.85879C246.855 0.619597 248.619 0 250.906 0C253.194 0 254.957 0.619597 256.197 1.85879C257.531 3.09799 258.198 4.90913 258.198 7.2922V76.9256C258.198 81.6917 259.152 85.2663 261.058 87.6494C263.06 89.9372 265.92 91.081 269.637 91.081C270.495 91.081 271.258 91.081 271.925 91.081C272.592 90.9857 273.259 90.8904 273.927 90.7951C275.071 90.6997 275.881 91.0334 276.357 91.796C276.834 92.4632 277.072 93.8931 277.072 96.0855C277.072 98.0873 276.643 99.6601 275.786 100.804C274.928 101.853 273.545 102.52 271.639 102.806C270.781 102.901 269.876 102.949 268.922 102.949C267.969 103.044 267.064 103.092 266.206 103.092Z"
				className={fill}
			/>
			<path
				d="M302.23 128.543C300.514 128.543 299.084 128.066 297.94 127.113C296.892 126.255 296.272 125.064 296.082 123.538C295.986 122.109 296.272 120.536 296.939 118.82L306.662 96.9434V103.092L279.924 40.8935C279.257 39.1777 279.019 37.5572 279.209 36.0321C279.4 34.5069 280.115 33.3154 281.354 32.4575C282.593 31.5042 284.261 31.0276 286.359 31.0276C288.17 31.0276 289.6 31.4566 290.648 32.3145C291.697 33.1724 292.65 34.6499 293.508 36.747L315.098 90.0801H311.095L332.971 36.747C333.829 34.5546 334.83 33.077 335.974 32.3145C337.118 31.4566 338.691 31.0276 340.693 31.0276C342.408 31.0276 343.743 31.5042 344.696 32.4575C345.745 33.3154 346.364 34.5069 346.555 36.0321C346.841 37.4619 346.603 39.0347 345.84 40.7505L310.094 122.967C309.141 125.159 308.045 126.636 306.805 127.399C305.661 128.162 304.136 128.543 302.23 128.543Z"
				className={fill}
			/>
			<path
				d="M391.488 103.092C385.768 103.092 380.955 102.091 377.046 100.089C373.138 98.0873 370.231 95.1323 368.324 91.224C366.418 87.3158 365.465 82.5496 365.465 76.9256V43.1813H356.886C354.979 43.1813 353.502 42.7047 352.453 41.7514C351.405 40.7029 350.88 39.3207 350.88 37.6049C350.88 35.7938 351.405 34.4116 352.453 33.4583C353.502 32.5051 354.979 32.0285 356.886 32.0285H365.465V17.3011C365.465 14.918 366.084 13.1069 367.323 11.8677C368.658 10.6285 370.469 10.0089 372.757 10.0089C375.045 10.0089 376.808 10.6285 378.047 11.8677C379.286 13.1069 379.906 14.918 379.906 17.3011V32.0285H396.635C398.542 32.0285 400.019 32.5051 401.068 33.4583C402.116 34.4116 402.641 35.7938 402.641 37.6049C402.641 39.3207 402.116 40.7029 401.068 41.7514C400.019 42.7047 398.542 43.1813 396.635 43.1813H379.906V75.7817C379.906 80.8338 380.955 84.6467 383.052 87.2205C385.244 89.7942 388.771 91.081 393.633 91.081C395.348 91.081 396.826 90.938 398.065 90.6521C399.4 90.2708 400.496 90.0325 401.354 89.9372C402.402 89.9372 403.26 90.3185 403.927 91.081C404.595 91.7483 404.928 93.0828 404.928 95.0846C404.928 96.5144 404.642 97.849 404.07 99.0882C403.594 100.327 402.688 101.138 401.354 101.519C400.21 101.9 398.637 102.234 396.635 102.52C394.729 102.901 393.013 103.092 391.488 103.092Z"
				className={fill}
			/>
			<path
				d="M414.229 99.6211C414.639 100.886 415.886 101.694 417.217 101.694H417.27C417.513 102.897 418.579 103.805 419.857 103.805C420.291 103.805 420.696 103.696 421.055 103.508C421.362 103.346 421.545 103.016 421.545 102.67V85.7805C421.545 85.333 421.237 84.935 420.799 84.8466C420.365 84.7583 419.881 84.8048 419.394 85.0376C418.687 85.3758 418.273 86.131 418.273 86.9167V86.9689C418.104 86.9366 417.923 86.9167 417.745 86.9167C416.288 86.9167 415.107 88.0985 415.107 89.5548C415.107 90.1676 415.317 90.7366 415.666 91.1803C414.157 91.413 412.995 92.7212 412.995 94.3048C412.995 95.4239 413.575 96.4053 414.451 96.9762C414.043 97.7087 413.909 98.635 414.229 99.6211ZM429.323 91.1803C429.673 90.7366 429.883 90.1676 429.883 89.5548C429.883 88.0985 428.701 86.9167 427.245 86.9167C427.066 86.9167 426.886 86.9366 426.716 86.9689V86.9167C426.716 86.132 426.301 85.3758 425.595 85.0376C425.108 84.8048 424.625 84.7583 424.19 84.8466C423.752 84.9359 423.445 85.334 423.445 85.7805V102.669C423.445 103.015 423.627 103.345 423.935 103.507C424.294 103.696 424.699 103.805 425.133 103.805C426.411 103.805 427.477 102.897 427.72 101.693H427.773C429.103 101.693 430.351 100.886 430.762 99.6201C431.082 98.634 430.948 97.7077 430.539 96.9753C431.414 96.4053 431.995 95.4239 431.995 94.3048C431.995 92.7212 430.833 91.413 429.323 91.1803Z"
				className={fill}
			/>
		</svg>
	);
};

Logo.GetCrewDone = GetCrewDone;
Logo.GetJobsDone = GetJobsDone;
Logo.Odylyt = Odylyt;
Logo.GetRecruitmentDone = GetRecruitmentDone;

export default Logo;
